import { Service } from '@genial/services';
import { FinanceEnumsPortabilitySortByEnum, MetaResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import { TableHeadItemOrderBy } from '../../../components/TableHead';
import { FiltersToApplyProps } from '../../../hooks/useFilterTransformer';
import { transformSortItem } from '../../../utils/transformSortItem';
import { screenName } from '../constants';

import {
  investmentsTransferPageState,
  investmentsTransferPeriodState,
  investmentsTransferSearchTermState,
} from './atom';
import {
  normalizeSecuritiesTransferDetailItem,
  normalizeSecuritiesTransferList,
  normalizeTransferStatusHistory,
} from './normalize';
import {
  InvestmentsTransferPeriodDates,
  SecuritiesTransferListItemNormalized,
  SecuritiesTransferDetailItemNormalized,
  TransferStatusHistoryItem,
} from './types';

export const updateInvestmentsTransferSearchTermSelector = selector<string>({
  key: 'updateInvestmentsTransferSearchTermSelector',
  set: ({ set }, value) => {
    set(investmentsTransferSearchTermState, value as string);
  },
  get: ({ get }) => {
    const params = get(investmentsTransferSearchTermState);
    return params;
  },
});

export const updateInvestmentsTransferPeriodSelector =
  selector<InvestmentsTransferPeriodDates | null>({
    key: 'updateInvestmentsTransferPeriodSelector',
    set: ({ set }, value) => {
      const newValue = value as InvestmentsTransferPeriodDates;
      set(investmentsTransferPeriodState, newValue);
    },
    get: ({ get }) => {
      const params = get(investmentsTransferPeriodState);
      return params;
    },
  });

export const updateInvestmentsTransferPageSelector = selector<number>({
  key: 'updateInvestmentsTransferPageSelector',
  set: ({ set }, value) => {
    const newValue = value as number;
    set(investmentsTransferPageState, newValue);
  },
  get: ({ get }) => {
    const params = get(investmentsTransferPageState);
    return params;
  },
});

export const investmentTransferRequestQuery = selectorFamily<
  { data: SecuritiesTransferListItemNormalized[]; meta: MetaResponse },
  {
    accountNumber: number;
    page: number;
    appliedOrderBy?: TableHeadItemOrderBy;
    searchTerm: string;
    startDate?: Date | null;
    endDate?: Date | null;
    filtersToApply: FiltersToApplyProps;
  }
>({
  key: 'customerDetail/investmentTransferRequestQuery',
  get: ({
    accountNumber,
    page,
    appliedOrderBy,
    searchTerm,
    startDate,
    endDate,
    filtersToApply,
  }) => {
    return async () => {
      const sortItems = transformSortItem(appliedOrderBy);

      const response = await Service.Finance.V1.getPortabilities({
        accountNumber,
        page,
        limit: 20,
        screenName,
        sortBy: sortItems?.sort as FinanceEnumsPortabilitySortByEnum,
        isDescending: sortItems?.sortDirection === 'desc',
        protocolNumber: searchTerm || undefined,
        startDate: startDate ? Utils.Mask.DateFormat(startDate, 'yyyy-MM-dd') : undefined,
        endDate: endDate ? Utils.Mask.DateFormat(endDate, 'yyyy-MM-dd') : undefined,
        ...filtersToApply,
      });

      return {
        data: normalizeSecuritiesTransferList(response.data),
        meta: response.meta,
      };
    };
  },
});

export const securitiesTransferDetailQuery = selectorFamily<
  { data: SecuritiesTransferDetailItemNormalized },
  { accountNumber: number; protocolNumber: string }
>({
  key: 'customerDetail/securitiesTransferDetailQuery',
  get: ({ accountNumber, protocolNumber }) => {
    return async () => {
      const { data } = await Service.Finance.V1.getPortabilitiesByAccountNumberProtocolNumber({
        screenName,
        accountNumber,
        protocolNumber,
      });

      return {
        data: normalizeSecuritiesTransferDetailItem(data),
      };
    };
  },
});

export const transferStatusHistoryQuery = selectorFamily<
  { data: TransferStatusHistoryItem[] },
  { protocolNumber: string; customerCpfCnpj: string }
>({
  key: 'securitiesTransferHistoryModal/transferStatusHistoryQuery',
  get: ({ protocolNumber, customerCpfCnpj }) => {
    return async () => {
      const { data } = await Service.Finance.V1.getPortabilitiesHistorical({
        limit: 100,
        screenName,
        protocolNumber,
        customerCpfCnpj,
      });

      const normalizedData = normalizeTransferStatusHistory(data);

      return { data: normalizedData };
    };
  },
});
